import { storageService } from '@/services/firebase/storage.service'
import { firestoreService } from '@/services/firebase/firestore.service'
import { createAsyncThunk, createAction } from '@reduxjs/toolkit'
import { orderBy } from 'firebase/firestore'
import { MediaItem, getMediaType, validateMediaFile } from '@/types/media.types'
import { RootState } from '@/app/store'
import { canManageMediaItem } from '@/utils/permissions'
import { formatFileName } from '@/utils/fileUtils'

const MEDIA_COLLECTION = 'media'
const MEDIA_STORAGE_ROOT = 'media'

/**
 * Acción standalone para actualizar el progreso de upload en el slice
 * sin generar dependencia circular slice ↔ thunks.
 */
export const setUploadProgress = createAction<number>('media/setUploadProgress')

export const fetchMediaItems = createAsyncThunk<MediaItem[], void, { rejectValue: string }>(
  'media/fetchMediaItems',
  async (_, { rejectWithValue }) => {
    try {
      const items = (await firestoreService.getDocuments(MEDIA_COLLECTION, [
        orderBy('createdAt', 'desc'),
      ])) as MediaItem[]
      return items
    } catch (error: any) {
      console.error('Error fetching media items:', error)
      return rejectWithValue(
        error.message || 'No se pudieron cargar los archivos de la biblioteca.'
      )
    }
  }
)

export interface UploadMediaItemParams {
  file: File
  name: string
}

export const uploadMediaItem = createAsyncThunk<
  MediaItem,
  UploadMediaItemParams,
  { rejectValue: string; state: RootState }
>(
  'media/uploadMediaItem',
  async ({ file, name }, { rejectWithValue, getState, dispatch }) => {
    try {
      if (!file) {
        return rejectWithValue('No se proporcionó archivo')
      }
      if (!name.trim()) {
        return rejectWithValue('El nombre del archivo es requerido')
      }
      const validationError = validateMediaFile(file)
      if (validationError) {
        return rejectWithValue(validationError)
      }

      const state = getState()
      const currentUser = state.auth.authUser
      if (!currentUser?.uid || !currentUser?.email) {
        return rejectWithValue('Sesión no válida. Por favor vuelve a iniciar sesión.')
      }

      const timestamp = Date.now()
      const safeFilename = formatFileName(file.name)
      const storagePath = `${MEDIA_STORAGE_ROOT}/${currentUser.uid}/${timestamp}_${safeFilename}`

      dispatch(setUploadProgress(0))
      const downloadUrl = await storageService.uploadFileWithProgress(
        storagePath,
        file,
        (progress) => {
          dispatch(setUploadProgress(progress))
        }
      )

      const createdByName =
        currentUser.displayName || currentUser.email || 'Usuario Desconocido'

      const newItem: Omit<MediaItem, 'id' | 'createdAt'> = {
        name: name.trim(),
        url: downloadUrl,
        storagePath,
        type: getMediaType(file.type),
        fileSize: file.size,
        mimeType: file.type,
        createdBy: currentUser.uid,
        createdByEmail: currentUser.email,
        createdByName,
      }

      const docId = (await firestoreService.addDocument(MEDIA_COLLECTION, newItem)) as string

      return {
        ...newItem,
        id: docId,
        createdAt: new Date().toISOString(),
      }
    } catch (error: any) {
      console.error('Error uploading media item:', error)
      return rejectWithValue(
        error.message || 'No se pudo subir el archivo. Por favor, intente nuevamente.'
      )
    }
  }
)

export interface DeleteMediaItemParams {
  id: string
  storagePath: string
}

export const deleteMediaItem = createAsyncThunk<
  string,
  DeleteMediaItemParams,
  { rejectValue: string; state: RootState }
>(
  'media/deleteMediaItem',
  async ({ id, storagePath }, { rejectWithValue, getState }) => {
    try {
      if (!id) return rejectWithValue('El ID del archivo es requerido')
      if (!storagePath) return rejectWithValue('La ruta de almacenamiento es requerida')

      const state = getState()
      const currentUser = state.auth.authUser
      const target = state.media.items.find((i) => i.id === id)
      if (!target) {
        return rejectWithValue('El archivo no existe o ya fue eliminado.')
      }
      if (!canManageMediaItem(target, currentUser)) {
        return rejectWithValue('No tienes permisos para eliminar este archivo.')
      }

      await firestoreService.deleteDocument(MEDIA_COLLECTION, id)

      try {
        await storageService.deleteFile(storagePath)
      } catch (storageError: any) {
        console.warn('Storage media deletion warning:', storageError)
        if (storageError.code !== 'storage/object-not-found') {
          throw storageError
        }
      }

      return id
    } catch (error: any) {
      console.error('Error deleting media item:', error)
      return rejectWithValue(
        error.message || 'Error al eliminar el archivo. Por favor, intente nuevamente.'
      )
    }
  }
)

export interface UpdateMediaItemNameParams {
  id: string
  newName: string
}

export interface UpdateMediaItemNameResponse {
  id: string
  name: string
  updatedAt: string
}

export const updateMediaItemName = createAsyncThunk<
  UpdateMediaItemNameResponse,
  UpdateMediaItemNameParams,
  { rejectValue: string; state: RootState }
>(
  'media/updateMediaItemName',
  async ({ id, newName }, { rejectWithValue, getState }) => {
    try {
      if (!id) return rejectWithValue('El ID del archivo es requerido')
      if (!newName.trim()) return rejectWithValue('El nombre del archivo es requerido')

      const state = getState()
      const currentUser = state.auth.authUser
      const target = state.media.items.find((i) => i.id === id)
      if (!target) {
        return rejectWithValue('El archivo no existe o ya fue eliminado.')
      }
      if (!canManageMediaItem(target, currentUser)) {
        return rejectWithValue('No tienes permisos para editar este archivo.')
      }

      await firestoreService.updateDocument(MEDIA_COLLECTION, id, {
        name: newName.trim(),
      })

      return {
        id,
        name: newName.trim(),
        updatedAt: new Date().toISOString(),
      }
    } catch (error: any) {
      console.error('Error updating media item:', error)
      return rejectWithValue(
        error.message || 'Error al actualizar el archivo. Por favor, intente nuevamente.'
      )
    }
  }
)
